/* eslint-disable react-native/no-unused-styles */

import React, { type ComponentProps } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';

import { theme } from '@garnish/constants';

import { useResponsive } from '../../../../hooks';
import { Icon } from '../../../Icon';
import { BodyText } from '../../../Text';
import {
  ICON_SIZE_MD,
  ICON_SIZE_SM,
  ICON_SIZE_XS,
  QUANTITY_STEPPER_SIZE_MD,
  QUANTITY_STEPPER_SIZE_SM,
  QUANTITY_STEPPER_SIZE_XS,
} from '../../IngredientCardV2.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const IngredientCardQuantity = (props: Props) => {
  const { children, iconName = 'IconCheck2', palette = 'kale', size, shouldUseIcon } = props;

  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const selectedSize = size ?? match(['xs', 'sm']);
  const iconSize = props.iconSize ?? ICON_SIZES[selectedSize];
  const paletteStyles = paletteStylesMap[palette];

  const allQuantityContainerStyles = [
    styles.quantityContainer,
    quantityContainerSizeStyles[selectedSize],
    paletteStyles.quantityContainer,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container} pointerEvents="none">
      <View style={allQuantityContainerStyles}>
        {shouldUseIcon ? (
          <Icon name={iconName} width={iconSize} height={iconSize} color={ICON_COLORS[palette]} />
        ) : (
          <BodyText
            sizeMatch={size === 'md' ? ['30'] : ['22']}
            maxFontSizeMultiplier={1.2}
            style={[styles.quantityText, paletteStyles.quantityText]}
          >
            {children}
          </BodyText>
        )}
      </View>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ICON_SIZES: Record<Size, number> = {
  xs: ICON_SIZE_XS,
  sm: ICON_SIZE_SM,
  md: ICON_SIZE_MD,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quantityContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  quantityText: {
    textAlign: 'center',
  },
});

const quantityContainerSizeStyles = StyleSheet.create<Record<Size, ViewStyle>>({
  xs: {
    width: QUANTITY_STEPPER_SIZE_XS,
    height: QUANTITY_STEPPER_SIZE_XS,
    borderRadius: QUANTITY_STEPPER_SIZE_XS,
  },
  sm: {
    width: QUANTITY_STEPPER_SIZE_SM,
    height: QUANTITY_STEPPER_SIZE_SM,
    borderRadius: QUANTITY_STEPPER_SIZE_SM,
  },
  md: {
    width: QUANTITY_STEPPER_SIZE_MD,
    height: QUANTITY_STEPPER_SIZE_MD,
    borderRadius: QUANTITY_STEPPER_SIZE_MD,
  },
});

// ─── Palette ────────────────────────────────────────────────────────────────

const kalePaletteStyles = StyleSheet.create({
  quantityContainer: {
    backgroundColor: theme.colors.LIME,
    borderColor: theme.colors.KALE,
  },
  quantityText: {
    color: theme.colors.KALE,
  },
});

const neutralPaletteStyles = StyleSheet.create({
  quantityContainer: {
    backgroundColor: theme.colors.OATMEAL,
    borderColor: theme.colors.OATMEAL,
  },
  quantityText: {
    color: theme.colors.DARK_KALE,
  },
});

const paletteStylesMap: Record<Palette, typeof kalePaletteStyles> = {
  kale: kalePaletteStyles,
  neutral: neutralPaletteStyles,
};

const ICON_COLORS: Record<Palette, string> = {
  kale: theme.colors.KALE,
  neutral: theme.colors.DARK_KALE,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  children?: string | number;
  iconName?: ComponentProps<typeof Icon>['name'];
  palette?: Palette;
  shouldUseIcon?: boolean;
  iconSize?: number;
  size?: Size;
};

type Size = 'xs' | 'sm' | 'md';

type Palette = 'kale' | 'neutral';
